/**
 * Installing the service worker, and noticing when a new one is ready.
 *
 * Registered from the application bundle rather than from a snippet in the HTML. That is not a
 * style preference: an inline script would need `unsafe-inline` in the Content Security Policy,
 * and the whole policy is built around not having it.
 *
 * The update flow is deliberately manual. A worker that calls `skipWaiting()` on install takes
 * over the page as soon as it is downloaded, and the usual next step is reloading the tab — under
 * someone who may be forty minutes into a timed exam. So a new version installs, waits, and says
 * so; the page decides when.
 */

export interface UpdateHandle {
  /** Activates the waiting worker and reloads once it has taken over. */
  readonly apply: () => void;
}

/**
 * Registers the worker, and calls back when a newer one is installed and waiting.
 *
 * Silent about failures on purpose. A service worker is an enhancement: no worker means the app
 * needs the network, which is exactly where it was before this file existed. Registration fails
 * routinely in private windows and behind some enterprise policies, and there is nothing the
 * person reading the screen could do about it.
 */
export function registerServiceWorker(onUpdateReady: (update: UpdateHandle) => void): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const url = `${import.meta.env.BASE_URL}sw.js`;

  const start = (): void => {
    void navigator.serviceWorker.register(url, { scope: import.meta.env.BASE_URL }).then(
      (registration) => {
        // Already waiting from a previous visit that never got round to reloading.
        if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
          onUpdateReady(handleFor(registration.waiting));
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (installing === null) {
            return;
          }
          installing.addEventListener('statechange', () => {
            // A worker that reaches `installed` with no controller is the *first* one: nothing is
            // being replaced, so there is nothing to tell anyone about.
            if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
              onUpdateReady(handleFor(installing));
            }
          });
        });
      },
      () => {
        // See above: an enhancement that did not happen.
      },
    );
  };

  // Waiting for `load` keeps the worker download from competing with the first screen — but only
  // if the page has not finished loading already. It usually has: this runs from an effect, and
  // React mounts after the document is parsed, so on a warm cache `load` has long since fired and
  // a listener added now would never be called.
  if (document.readyState === 'complete') {
    start();
  } else {
    window.addEventListener('load', start, { once: true });
  }
}

function handleFor(worker: ServiceWorker): UpdateHandle {
  return {
    apply: () => {
      // Reload when the new worker actually controls the page, not when it was asked to — the
      // order matters, or the reload serves the old shell one last time.
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          window.location.reload();
        },
        { once: true },
      );
      worker.postMessage('SKIP_WAITING');
    },
  };
}
